import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, X, Award, Pencil, CheckCircle, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditPackageModal = ({ onClose, onEdit, pkg }) => {
  const [formData, setFormData] = useState(pkg);

  const handleSubmit = (e) => {
    e.preventDefault();
    onEdit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-text_primary mb-6">Edit Package</h2>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Package Name</label>
            <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Duration (Months)</label>
              <input required type="number" min="0" value={formData.duration_months} onChange={e=>setFormData({...formData, duration_months: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Price (₹)</label>
              <input required type="number" min="0" value={formData.price} onChange={e=>setFormData({...formData, price: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Included PT Sessions</label>
            <input required type="number" min="0" value={formData.pt_sessions || 0} onChange={e=>setFormData({...formData, pt_sessions: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Package Type</label>
            <select data-testid="edit-package-type" value={formData.type || "Membership"} onChange={e=>setFormData({...formData, type: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
              <option value="Membership">Membership (base plan)</option>
              <option value="Add-on">Add-on (Transformation / PT)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Category</label>
              <select value={formData.category || "Monthly"} onChange={e=>setFormData({...formData, category: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                {["Monthly","Quarterly","Half-Yearly","Annual","Day Pass","PT Package"].map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">GST %</label>
              <input type="number" min="0" value={formData.gst_percent || 0} onChange={e=>setFormData({...formData, gst_percent: parseFloat(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Description</label>
            <input type="text" value={formData.description || ""} onChange={e=>setFormData({...formData, description: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Changes</button>
        </form>
      </div>
    </div>
  );
};

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [formData, setFormData] = useState({ name: "", duration_months: 1, price: 0, pt_sessions: 0, gst_percent: 18, discount: 0, discount_type: "percent", category: "Monthly", description: "", type: "Membership" });

  const fetchPackages = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/packages`);
      setPackages(res.data);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/packages`, formData);
      setShowModal(false);
      setFormData({ name: "", duration_months: 1, price: 0, pt_sessions: 0, gst_percent: 18, discount: 0, discount_type: "percent", category: "Monthly", description: "", type: "Membership" });
      fetchPackages();
    } catch(e) { console.error(e); }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/update/packages/${data.id}`, data);
      setShowEdit(null);
      fetchPackages();
    } catch(e) { alert(e.response?.data?.detail || "Admin access required to edit."); }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Are you sure you want to delete this package?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/delete/packages/${id}`);
        fetchPackages();
      } catch(e) { alert(e.response?.data?.detail || "Admin access required to delete."); }
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 relative">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Packages</h1>
          <p className="text-text_secondary text-sm">Configure membership tiers, pricing, and PT sessions.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl font-bold hover:bg-primary_hover transition-colors flex items-center gap-2 shadow-md">
          <Plus size={20} /> Create Package
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {packages.length === 0 && <p className="text-text_secondary col-span-full">No packages configured. Create one to get started.</p>}
        {packages.map((pkg) => (
          <div key={pkg.id} className="bg-card_bg border border-border_soft rounded-2xl p-8 shadow-sm flex flex-col justify-between group transition-all duration-300 hover:border-primary/40 hover:-translate-y-1">
            <div>
              <div className="flex justify-between items-start mb-6">
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center text-gold border border-primary/20">
                  <Award size={28} strokeWidth={1.5} />
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => setShowEdit(pkg)} className="p-2 bg-card_bg rounded-lg text-text_secondary hover:text-gold transition-colors" title="Edit"><Pencil size={16} /></button>
                  <button onClick={() => handleDelete(pkg.id)} className="p-2 bg-card_bg rounded-lg text-text_secondary hover:text-accent_danger transition-colors" title="Delete"><Trash2 size={16} /></button>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-text_primary mb-2 tracking-tight">{pkg.name}</h3>
              <div className="flex gap-2 items-center mb-3 flex-wrap">
                {pkg.type === "Add-on" && <span className="bg-gold/20 text-gold border border-gold/40 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-widest uppercase">Add-on</span>}
                {pkg.category && <span className="bg-olive/20 text-olive border border-olive/30 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-widest uppercase">{pkg.category}</span>}
                <p className="text-text_secondary text-sm font-medium">{pkg.duration_months} Months</p>
                {pkg.pt_sessions > 0 && (
                  <span className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/20 text-gold px-2.5 py-1 rounded-md text-[10px] font-bold tracking-widest uppercase">
                    <CheckCircle size={12}/> {pkg.pt_sessions} PT
                  </span>
                )}
              </div>
              {pkg.description && <p className="text-text_secondary text-sm mb-3">{pkg.description}</p>}
            </div>
            <div className="border-t border-border_soft/50 pt-5 mt-2 flex justify-between items-end">
              <span className="text-xs text-text_secondary uppercase tracking-widest font-semibold">{pkg.gst_percent > 0 ? `+${pkg.gst_percent}% GST` : "Pricing"}</span>
              <span className="text-4xl font-bold text-gold tracking-tight">₹{pkg.price.toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>

      {showEdit && <EditPackageModal pkg={showEdit} onClose={() => setShowEdit(null)} onEdit={handleEdit} />}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl">
            <button onClick={() => setShowModal(false)} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
            <h2 className="text-2xl font-serif text-text_primary mb-6">Create Package</h2>
            <form onSubmit={handleCreate} className="space-y-5">
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Package Name</label>
                <input required type="text" value={formData.name} onChange={e=>setFormData({...formData, name: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Duration (Months)</label>
                  <input required type="number" min="0" value={formData.duration_months} onChange={e=>setFormData({...formData, duration_months: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Price (₹)</label>
                  <input required type="number" min="0" value={formData.price} onChange={e=>setFormData({...formData, price: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Included PT Sessions</label>
                <input required type="number" min="0" value={formData.pt_sessions} onChange={e=>setFormData({...formData, pt_sessions: parseInt(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Package Type</label>
                <select data-testid="package-type" value={formData.type} onChange={e=>setFormData({...formData, type: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                  <option value="Membership">Membership (base plan)</option>
                  <option value="Add-on">Add-on (Transformation / PT)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Category</label>
                  <select value={formData.category} onChange={e=>setFormData({...formData, category: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                    {["Monthly","Quarterly","Half-Yearly","Annual","Day Pass","PT Package"].map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">GST %</label>
                  <input type="number" min="0" value={formData.gst_percent} onChange={e=>setFormData({...formData, gst_percent: parseFloat(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Discount</label>
                  <input type="number" min="0" value={formData.discount} onChange={e=>setFormData({...formData, discount: parseFloat(e.target.value)})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Discount Type</label>
                  <select value={formData.discount_type} onChange={e=>setFormData({...formData, discount_type: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                    <option value="percent">Percent (%)</option>
                    <option value="flat">Flat (₹)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Description</label>
                <input type="text" value={formData.description} onChange={e=>setFormData({...formData, description: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Package</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
